<template>
  <div ref="rfpRatingsCarousel" class="rfp-ratings-carousel">
    <div class="rfp-ratings-carousel-wrapper">
      <div
        class="rfp-ratings-carousel-inner"
        :style="`

          transform: translateX(${currentOffset}px);

          transition: ${transitionStyle};

          flex-basis: ${slideWidth}px;

          visibility: ${slideWidth ? 'visible' : 'hidden'}`"
      >
        <slot :current-page="currentPage"></slot>
      </div>
      <rfp-ratings-pagination v-if="paginationEnabled && pageCount > 0"> </rfp-ratings-pagination>
    </div>
    <rfp-ratings-navigation
      v-if="navigationEnabled"
      :click-target-size="navigationClickTargetSize"
      :can-advance-backward="canAdvanceBackward"
      :can-advance-forward="canAdvanceForward"
      @navigate-next="advancePage('next')"
      @navigate-prev="advancePage('prev')"
    ></rfp-ratings-navigation>
  </div>
</template>

<script>
import eventBus from '@refapp/eventbus-js';
import autoplay from './mixins/rfp-ratings-autoplay';
import RfpRatingsNavigation from './rfp-ratings-navigation.vue';
import RfpRatingsPagination from './rfp-ratings-pagination.vue';

export default {
  name: 'rfp-ratings-carousel',
  components: {
    RfpRatingsNavigation,
    RfpRatingsPagination,
  },
  mixins: [autoplay],
  props: {
    easing: {
      type: String,
      default: 'ease',
    },
    minSwipeDistance: {
      type: Number,
      default: 8,
    },
    navigationClickTargetSize: {
      type: Number,
      default: 8,
    },
    navigationEnabled: {
      type: Boolean,
      default: true,
    },
    paginationActiveColor: {
      type: String,
      default: '#000000',
    },
    paginationColor: {
      type: String,
      default: '#efefef',
    },
    paginationEnabled: {
      type: Boolean,
      default: true,
    },
    paginationPadding: {
      type: Number,
      default: 10,
    },
    paginationSize: {
      type: Number,
      default: 10,
    },
    perPage: {
      type: Number,
      default: 1,
    },
    perPageCustom: {
      type: Array,
      default() {
        return [];
      },
    },
    scrollPerPage: {
      type: Boolean,
      default: false,
    },
    speed: {
      type: Number,
      default: 500,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    reviewCount: {
      type: Number,
      default: 0,
    },
    resetResultsApiRequestCount: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      browserWidth: null,
      carouselWidth: null,
      currentPage: 0,
      dragOffset: 0,
      dragStartX: 0,
      mousedown: false,
      slideCount: 0,
    };
  },
  computed: {
    breakpointSlidesPerPage() {
      if (!this.perPageCustom) {
        return this.perPage;
      }

      const breakpointArray = this.perPageCustom;
      const width = this.browserWidth;

      const breakpoints = breakpointArray.sort((a, b) => (a[0] > b[0] ? -1 : 1));
      const matches = breakpoints.filter((breakpoint) => width >= breakpoint[0]);

      const match = matches[0] && matches[0][1];

      return match || this.perPage;
    },
    canAdvanceForward() {
      return this.loop || this.currentPage < this.pageCount - 1;
    },
    canAdvanceBackward() {
      return this.loop || this.currentPage > 0;
    },
    currentPerPage() {
      return !this.perPageCustom ? this.perPage : this.breakpointSlidesPerPage;
    },
    currentOffset() {
      const page = this.currentPage;
      const width = this.slideWidth;
      const dragged = this.dragOffset;

      const offset = this.scrollPerPage ? page * width * this.currentPerPage : page * width;

      return (offset + dragged) * -1;
    },
    isHidden() {
      return this.carouselWidth <= 0;
    },

    pageCount() {
      const slideCount = this.slideCount;
      const perPage = this.currentPerPage;

      if (this.scrollPerPage) {
        const pages = Math.ceil(slideCount / perPage);
        return pages < 1 ? 1 : pages;
      }

      return slideCount - (this.currentPerPage - 1);
    },

    slideWidth() {
      const width = this.carouselWidth;
      const perPage = this.currentPerPage;

      return width / perPage;
    },
    transitionStyle() {
      return `${this.speed / 1000}s ${this.easing} transform`;
    },
  },
  watch: {
    resetResultsApiRequestCount() {
      this.currentPage = 0;
    },
  },
  beforeUpdate() {
    this.computeCarouselWidth();
  },
  mounted() {
    if ('ontouchstart' in window) {
      this.$refs.rfpRatingsCarousel.addEventListener('touchstart', this.handleMousedown);
      this.$refs.rfpRatingsCarousel.addEventListener('touchend', this.handleMouseup);
      this.$refs.rfpRatingsCarousel.addEventListener('touchmove', this.handleMousemove);
    } else {
      this.$refs.rfpRatingsCarousel.addEventListener('mousedown', this.handleMousedown);
      this.$refs.rfpRatingsCarousel.addEventListener('mouseup', this.handleMouseup);
      this.$refs.rfpRatingsCarousel.addEventListener('mousemove', this.handleMousemove);
    }

    this.attachMutationObserver();
    this.computeCarouselWidth();
  },
  destroyed() {
    this.detachMutationObserver();
    window.removeEventListener('resize', this.getBrowserWidth);
    if ('ontouchstart' in window) {
      this.$refs.rfpRatingsCarousel.removeEventListener('touchmove', this.handleMousemove);
    } else {
      this.$refs.rfpRatingsCarousel.removeEventListener('mousemove', this.handleMousemove);
    }
  },
  methods: {
    getNextPage() {
      if (this.currentPage < this.pageCount - 1) {
        return this.currentPage + 1;
      }
      return this.loop ? 0 : this.currentPage;
    },
    getPreviousPage() {
      if (this.currentPage > 0) {
        return this.currentPage - 1;
      }
      return this.loop ? this.pageCount - 1 : this.currentPage;
    },
    advancePage(direction) {
      if (direction && direction === 'prev' && this.canAdvanceBackward) {
        this.goToPage(this.getPreviousPage());
      } else if ((!direction || (direction && direction !== 'prev')) && this.canAdvanceForward) {
        eventBus.emit('dataGetFromDB', this.currentPage);
        this.goToPage(this.getNextPage());
      }
    },
    attachMutationObserver() {
      const MutationObserver =
        window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver;

      if (MutationObserver) {
        const config = { attributes: true, data: true };
        this.mutationObserver = new MutationObserver(() => {
          this.$nextTick(() => {
            this.computeCarouselWidth();
          });
        });
        if (this.$parent.$el) {
          this.mutationObserver.observe(this.$parent.$el, config);
        }
      }
    },
    detachMutationObserver() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    },
    getBrowserWidth() {
      this.browserWidth = window.innerWidth;
      return this.browserWidth;
    },
    getCarouselWidth() {
      this.carouselWidth = this.$refs?.rfpRatingsCarousel.clientWidth || 0;
      return this.carouselWidth;
    },
    getSlideCount() {
      this.slideCount = this.reviewCount;
    },
    goToPage(page) {
      if (page >= 0 && page <= this.pageCount) {
        this.currentPage = page;
        this.$emit('pageChange', this.currentPage);
        eventBus.emit('rfp-global-page-change', this.currentPage);
      }
    },
    handleMousedown(e) {
      if (!e.touches) {
        e.preventDefault();
      }

      this.mousedown = true;
      this.dragStartX = 'ontouchstart' in window ? e.touches[0].clientX : e.clientX;
    },
    handleMouseup() {
      this.mousedown = false;
      this.dragOffset = 0;
    },
    handleMousemove(e) {
      if (!this.mousedown) {
        return;
      }

      const eventPosX = 'ontouchstart' in window ? e.touches[0].clientX : e.clientX;
      const deltaX = this.dragStartX - eventPosX;

      this.dragOffset = deltaX;

      if (this.dragOffset > this.minSwipeDistance) {
        this.handleMouseup();
        this.advancePage('next');
      } else if (this.dragOffset < -this.minSwipeDistance) {
        this.handleMouseup();
        this.advancePage('prev');
      }
    },
    computeCarouselWidth() {
      this.getSlideCount();
      this.getBrowserWidth();
      this.getCarouselWidth();
      this.setCurrentPageInBounds();
    },
    setCurrentPageInBounds() {
      if (!this.canAdvanceForward) {
        const setPage = this.pageCount - 1;
        this.currentPage = setPage >= 0 ? setPage : 0;
      }
    },
    resetCurrentPage() {
      this.currentPage = 0;
    },
  },
};
</script>
